import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, History, Search, Shuffle, ToggleLeft, Upload, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "@/components/common/States";
import { useAuth, useSchoolCan, useSpaceName } from "@/context/AuthContext";
import { api, errorMessage, fetcher } from "@/lib/api";
import { MEMBER_STATUS, REVIEW_STATUS, ROLE_LABELS, formatDate } from "@/lib/labels";
import { AddMemberDialog } from "./AddMemberDialog";
import { ImportDialog } from "@/components/feed/ImportDialog";
import { ResetLinkDialog } from "@/components/settings/ResetLinkDialog";
import { HistoryDialog, RoleDialog, StatusDialog } from "./MemberDialogs";
import { ReadOnlyBanner } from "./SchoolHome";

const SEARCH_FIELDS = ["full_name", "nisn", "nip", "employee_registration_number", "student_registration_number", "email"];

const SORTS = {
  created: { label: "Urutan dibuat" },
  full_name: { label: "Nama" },
  role: { label: "Peran" },
  status: { label: "Status" },
  valid_from: { label: "Berlaku sejak" },
};

const sortValue = (m, key) => {
  if (key === "full_name") return (m.full_name || "").toLowerCase();
  if (key === "role") return ROLE_LABELS[m.role] || "";
  if (key === "status") return m.status || "";
  if (key === "valid_from") return m.valid_from ? new Date(m.valid_from).getTime() : 0;
  return 0;
};

const compareMembers = (a, b, key) => {
  const va = sortValue(a, key);
  const vb = sortValue(b, key);
  if (typeof va === "number" && typeof vb === "number") return va - vb;
  return String(va).localeCompare(String(vb), "id-ID");
};

export const matchMember = (m, q) => {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return SEARCH_FIELDS.some((f) => (m[f] || "").toLowerCase().includes(n));
};

const exportCsv = async () => {
  try {
    const r = await api.get("/school/members-export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: "anggota-sekolah.csv" });
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(errorMessage(e));
  }
};

const Reviews = () => {
  const q = useQuery({ queryKey: ["school-reviews"], queryFn: () => fetcher("/school/identity-reviews") });
  if (!q.data?.length) return null;
  return (
    <section className="space-y-3" data-testid="school-reviews">
      <h2 className="text-base md:text-lg font-bold">Pengajuan tinjauan identitas</h2>
      <ul className="rounded-xl border divide-y">
        {q.data.map((r) => (
          <li key={r.id} className="flex items-center justify-between p-4 text-sm">
            <span>{r.submitted.full_name} · {ROLE_LABELS[r.submitted.role]} · {formatDate(r.created_at)}</span>
            <StatusBadge map={REVIEW_STATUS} value={r.status} />
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function SchoolMembers() {
  const { me } = useAuth();
  const space = useSpaceName();
  const canCreate = useSchoolCan("members:create");
  const canUpdate = useSchoolCan("members:update");
  const canExport = useSchoolCan("members:export");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["school-members"], queryFn: () => fetcher("/school/members") });
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [statusM, setStatusM] = useState(null);
  const [roleM, setRoleM] = useState(null);
  const [histId, setHistId] = useState(null);
  const [resetHasil, setResetHasil] = useState(null);
  const [cari, setCari] = useState("");
  const [sort, setSort] = useState({ key: "created", dir: "asc" });
  const [filterRole, setFilterRole] = useState("semua");
  const [filterStatus, setFilterStatus] = useState("semua");
  const done = (msg) => { toast.success(msg); qc.invalidateQueries(); };
  const editable = (m) => canUpdate && m.role !== "school_admin" && m.user_id !== me.user.id;

  const anggota = useMemo(() => {
    const data = q.data || [];
    const filtered = data.filter((m) =>
      matchMember(m, cari) &&
      (filterRole === "semua" || m.role === filterRole) &&
      (filterStatus === "semua" || m.status === filterStatus));
    if (sort.key === "created") return filtered;
    const sorted = [...filtered].sort((a, b) => compareMembers(a, b, sort.key));
    return sort.dir === "desc" ? sorted.reverse() : sorted;
  }, [q.data, cari, filterRole, filterStatus, sort]);

  const peranTersedia = useMemo(() => [...new Set((q.data || []).map((m) => m.role))], [q.data]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const sortHeader = (key, label) => (
    <button type="button" onClick={() => toggleSort(key)} aria-label={`Urutkan berdasarkan ${label}`}
      data-testid={`members-sort-${key}`} className="inline-flex items-center gap-1 font-medium hover:text-foreground">
      {label}
      {sort.key === key
        ? (sort.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />)
        : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}
    </button>
  );

  const histM = useMemo(() => (q.data || []).find((m) => m.id === histId) || null, [q.data, histId]);
  const openDetail = (m) => setHistId(m.id);
  const openStatus = (m) => { setHistId(null); setStatusM(m); };
  const openRole = (m) => { setHistId(null); setRoleM(m); };
  const kirimReset = async (m) => {
    try {
      const { data } = await api.post(`/school/members/${m.id}/reset-password`);
      setHistId(null);
      setResetHasil({ ...data, nama: m.full_name });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const actions = (m, s = "") => (
    <>
      {editable(m) && <>
        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setStatusM(m); }} aria-label="Ubah status" title="Ubah status" data-testid={`member-status-button-${m.id}${s}`}><ToggleLeft className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setRoleM(m); }} aria-label="Ubah peran" title="Ubah peran" data-testid={`member-role-button-${m.id}${s}`}><Shuffle className="h-4 w-4" /></Button>
      </>}
      <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setHistId(m.id); }} aria-label="Lihat detail dan riwayat" title="Detail dan riwayat" data-testid={`member-history-button-${m.id}${s}`}><History className="h-4 w-4" /></Button>
    </>
  );

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={space} title="Anggota Sekolah" description="Keanggotaan hanya berlaku di sekolah ini. Data akademik tidak dibagikan lintas sekolah."
        actions={<>
          {canExport && <Button variant="outline" onClick={exportCsv} data-testid="members-export-button"><Download className="h-4 w-4 mr-1" /> Ekspor CSV</Button>}
          {canCreate && <Button variant="outline" onClick={() => setImporting(true)} data-testid="members-import-button"><Upload className="h-4 w-4 mr-1" /> Impor Massal</Button>}
          {canCreate && <Button onClick={() => setAdding(true)} data-testid="members-add-button"><UserPlus className="h-4 w-4 mr-1" /> Tambah anggota</Button>}
        </>} />
      <ReadOnlyBanner />
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : !q.data.length ? <EmptyState title="Belum ada anggota" description="Tambahkan anggota atau kirim undangan agar mereka dapat membuat akun." /> : (
        <>
        <div className="flex flex-col gap-3 md:flex-row md:items-center" data-testid="members-filters">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={cari} onChange={(e) => setCari(e.target.value)} aria-label="Cari anggota"
              placeholder="Cari nama, NISN, NIP, NUPTK/NITK/NRG, NIS/NIPD/NISM/NISD, atau email…"
              className="pl-9" data-testid="members-search-input" />
          </div>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger aria-label="Filter peran" className="md:w-48" data-testid="members-role-filter"><SelectValue placeholder="Semua peran" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua">Semua peran</SelectItem>
              {peranTersedia.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger aria-label="Filter status" className="md:w-40" data-testid="members-status-filter"><SelectValue placeholder="Semua status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua">Semua status</SelectItem>
              {Object.entries(MEMBER_STATUS).map(([v, s]) => <SelectItem key={v} value={v}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {anggota.length === 0 ? (
          <EmptyState title="Tidak ada anggota yang cocok" description="Coba ubah kata kunci pencarian atau filter." />
        ) : (<>
        <p className="text-sm text-muted-foreground" data-testid="members-count">Menampilkan {anggota.length} dari {q.data.length} anggota</p>
        <ul className="lg:hidden space-y-3" data-testid="member-cards">
          {anggota.map((m, i) => (
            <li key={m.id} className="rounded-xl border bg-card p-4 cursor-pointer hover:bg-muted/50" onClick={() => openDetail(m)} data-testid={`member-card-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold"><span className="text-muted-foreground font-normal mr-1">{i + 1}.</span>{m.full_name}</p>
                  <p className="text-xs text-muted-foreground">{ROLE_LABELS[m.role]} · {m.email || "Belum punya akun"}</p>
                </div>
                <StatusBadge map={MEMBER_STATUS} value={m.status} />
              </div>
              <div className="flex justify-end mt-2">{actions(m, "-m")}</div>
            </li>
          ))}
        </ul>
        <div className="hidden lg:block rounded-xl border overflow-x-auto">
          <Table data-testid="members-table">
            <TableHeader><TableRow>
              <TableHead className="w-12">No.</TableHead>
              <TableHead>{sortHeader("full_name", "Nama")}</TableHead>
              <TableHead>{sortHeader("role", "Peran")}</TableHead>
              <TableHead>{sortHeader("status", "Status")}</TableHead>
              <TableHead>NISN/NIP</TableHead>
              <TableHead>Akun</TableHead>
              <TableHead>{sortHeader("valid_from", "Berlaku sejak")}</TableHead>
              <TableHead className="text-right">Tindakan</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {anggota.map((m, i) => (
                <TableRow key={m.id} className="cursor-pointer" onClick={() => openDetail(m)} data-testid={`member-row-${m.id}`}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">{m.full_name}</TableCell>
                  <TableCell>{ROLE_LABELS[m.role]}</TableCell>
                  <TableCell><StatusBadge map={MEMBER_STATUS} value={m.status} testId={`member-status-${m.id}`} /></TableCell>
                  <TableCell className="font-mono text-xs">{m.nisn || m.nip || "—"}</TableCell>
                  <TableCell className="text-xs">{m.email || "Belum punya akun"}</TableCell>
                  <TableCell className="text-xs">{formatDate(m.valid_from)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{actions(m)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>)}
        </>
      )}
      <Reviews />
      <AddMemberDialog open={adding} onOpenChange={setAdding} roles={me.grantable_roles}
        onDone={(d) => { d.review_created ? toast.warning(d.message, { duration: 8000 }) : toast.success(d.message); qc.invalidateQueries(); }} />
      <ImportDialog open={importing} onOpenChange={setImporting} onDone={() => qc.invalidateQueries()} />
      <StatusDialog member={statusM} onClose={() => setStatusM(null)} onDone={done} />
      <RoleDialog member={roleM} roles={me.grantable_roles} onClose={() => setRoleM(null)} onDone={done} />
      <HistoryDialog
        member={histM}
        onClose={() => setHistId(null)}
        canEdit={!!histM && editable(histM)}
        roles={me.grantable_roles}
        onEditStatus={openStatus}
        onEditRole={openRole}
        onResetPassword={kirimReset}
        onSaved={done}
      />
      <ResetLinkDialog hasil={resetHasil} onClose={() => setResetHasil(null)} />
    </div>
  );
}
